import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ImagePlus,
  RefreshCw,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { Orgs } from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";
import { useNotifications } from "../../shared/NotificationsProvider";

const LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
const LOGO_MAX_BYTES = 1_500_000;

type Props = {
  orgId: string;
  initialLogoUrl: string | null;
  onContinue: () => void;
  onBack: () => void;
};

export function StepLogo({ orgId, initialLogoUrl, onContinue, onBack }: Props) {
  const { run } = useApiCall();
  const { toast } = useNotifications();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Mantén el preview sincronizado si el snapshot cambia mientras el step
  // está montado (p.ej. tras un upload concurrente desde otra pestaña).
  useEffect(() => {
    setLogoUrl(initialLogoUrl);
  }, [initialLogoUrl]);

  async function uploadFile(file: File) {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast("Formato no soportado. Usa PNG, JPEG o WebP.", "error");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast(
        `El logo no puede superar ${Math.round(LOGO_MAX_BYTES / 1024)} KB.`,
        "error",
      );
      return;
    }
    setBusy(true);
    const res = await run(() => Orgs.uploadLogo(orgId, file), {
      successMessage: "Logo guardado.",
    });
    setBusy(false);
    if (res?.logoUrl) setLogoUrl(res.logoUrl);
  }

  async function deleteLogo() {
    setBusy(true);
    const res = await run(() => Orgs.deleteLogo(orgId), {
      successMessage: "Logo eliminado.",
    });
    setBusy(false);
    if (res) setLogoUrl(null);
  }

  function onPickerChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void uploadFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void uploadFile(file);
  }

  return (
    <section className="space-y-10">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-wider text-ink-3">
          Tu marca
        </p>
        <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-ink leading-[1.05]">
          Sube el logo
          <br />
          de tu empresa.
        </h1>
        <p className="text-base sm:text-lg text-ink-2 max-w-xl">
          Lo verás en la esquina superior de la app y aparecerá en los
          informes PDF que generes. Lo puedes cambiar cuando quieras.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={LOGO_ACCEPT}
        className="hidden"
        onChange={onPickerChange}
      />

      {logoUrl ? (
        <div className="space-y-6">
          <div
            className="w-full max-w-md aspect-[3/1] rounded-2xl bg-surface-2 border border-border overflow-hidden flex items-center justify-center"
            aria-label="Vista previa del logo"
          >
            <img
              src={logoUrl}
              alt="Logo subido"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-surface-2 text-ink text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              Cambiar logo
            </button>
            <button
              type="button"
              onClick={deleteLogo}
              disabled={busy}
              className="inline-flex items-center gap-2 text-ink-3 text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Quitar
            </button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`w-full max-w-md min-h-[180px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${
            dragOver
              ? "border-ink bg-surface-2"
              : "border-border bg-transparent hover:bg-surface-2/50"
          } ${busy ? "opacity-60 pointer-events-none" : ""}`}
          aria-label="Subir logo"
        >
          {busy ? (
            <>
              <UploadCloud className="w-8 h-8 text-ink-3 animate-pulse" />
              <p className="text-sm font-semibold text-ink">Subiendo…</p>
            </>
          ) : (
            <>
              <ImagePlus className="w-8 h-8 text-ink-3" />
              <p className="text-sm font-semibold text-ink">
                Arrastra una imagen o haz clic para subirla
              </p>
              <p className="text-xs text-ink-3">
                PNG, JPEG o WebP hasta {Math.round(LOGO_MAX_BYTES / 1024)} KB
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="inline-flex items-center gap-2 text-ink-3 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          Atrás
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-ink text-white text-base font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:scale-[1.02] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {logoUrl ? "Continuar" : "Saltar por ahora"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
